/**
 * Centralized alert/toast messages - single source of truth for notification text.
 *
 * Contains all user-facing text used in toast notifications and alerts.
 * Organized by operation type for easy discovery.
 *
 * @example
 * import { alertMessages } from '@/constants';
 * showAlert(alertMessages.archive.success('Packets'), 'success');
 */

/**
 * #1185 (owner Q-296 a): what a person reads when the server fails. vingapi
 * (#1742) sends exactly this as the `detail` of every 5xx; the client shows it
 * when no usable answer arrived (empty body, HTML/text from a proxy, no
 * `detail`). `extractApiMessage` applies it to every 5xx. Defined once here
 * (src/__tests__/serverFailureSentence.guard.test.ts).
 */
export const SERVER_FAILURE_MESSAGE = 'Something went wrong on our side. Please try again.';

/**
 * Helper to lowercase entity name for error messages
 */
function lowercase(entityName: string): string {
  return entityName.toLowerCase();
}

export const alertMessages = {
  /**
   * Archive/unarchive operations
   */
  archive: {
    success: (entityName: string) => `${entityName} archived successfully`,
    error: (entityName: string) => `Failed to archive ${lowercase(entityName)}`,
    unarchiveSuccess: (entityName: string) => `${entityName} unarchived successfully`,
    unarchiveError: (entityName: string) => `Failed to unarchive ${lowercase(entityName)}`,
  },

  /**
   * Folder/category operations
   */
  folder: {
    createError: 'Failed to create folder',
    renameSuccess: 'Folder renamed',
    renameError: 'Failed to rename folder',
    deleteSuccess: 'Folder deleted',
    deleteError: 'Failed to delete folder',
    moveLabelsError: 'Failed to move labels',
  },

  /**
   * Submission operations
   */
  submission: {
    resolveSuccess: 'Submissions resolved successfully',
    resolveError: 'Failed to resolve submissions',
    responseResolveSuccess: 'Responses resolved successfully',
    responseResolveError: 'Failed to resolve responses',
  },

  /**
   * Data export operations (assignments / submissions export — async, emailed)
   */
  dataExport: {
    queuedSuccess: 'Export started — it will be emailed to you when it is ready.',
    /** Transient bell popover shown the moment an export is queued. */
    queuedNotice: 'Export started — we’ll notify you here when it’s ready.',
    error: 'There was an error requesting the export.',
  },

  /**
   * Data import operations (contacts / assignments import — async, processed
   * in the background and reported in the activity stream).
   */
  dataImport: {
    /** Transient bell popover shown the moment an import is queued. */
    queuedNotice: 'Import started — we’ll notify you here when it’s done.',
  },

  /**
   * Assignment operations
   */
  assignment: {
    inviteSentSuccess: 'Invitations sent successfully',
    inviteSentError: 'Failed to send invitations',
    completeSuccess: 'Assignments marked as complete',
    completeError: 'Failed to complete assignments',
    removeSuccess: 'Assignments removed successfully',
    removeError: 'Failed to remove assignments',
    setRequiredSuccess: 'Assignment required flag updated',
    setRequiredError: 'Failed to update required flag',
    setDatesSuccess: 'Assignment dates updated successfully',
    setDatesError: 'Failed to update assignment dates',
  },

  /**
   * Generic error messages
   */
  generic: {
    error: 'An error occurred',
    networkError: 'Network error. Please try again.',
    saveError: 'Failed to save changes',
    loadError: 'Failed to load data',
  },

  /**
   * Label operations
   */
  label: {
    createSuccess: 'Label created',
    createError: 'Failed to create label',
    updateSuccess: 'Label updated',
    updateError: 'Failed to update label',
    deleteSuccess: 'Label deleted',
    deleteError: 'Failed to delete label',
    assignSuccess: 'Labels assigned',
    assignError: 'Failed to assign labels',
    labelsUpdated: 'Labels successfully updated.',
  },

  /**
   * Packet/Ving operations
   */
  packet: {
    createSuccess: 'Packet created successfully',
    createError: 'Failed to create packet',
    copySuccess: 'Packet copied successfully',
    copyError: 'Failed to copy packet',
    reviseError: 'Failed to create revision',
    publishSuccess: 'Packet published successfully',
    publishError: 'Failed to publish packet',
    deleteSuccess: 'Ving deleted successfully',
    deleteError: 'Failed to delete Ving',
    archiveSuccess: 'Ving archived successfully',
    archiveError: 'Failed to archive ving',
    unarchiveSuccess: 'Ving unarchived successfully',
    unarchiveError: 'Failed to unarchive ving',
    ownerChangedSuccess: 'Owner changed successfully',
    ownerChangedError: 'Failed to change owner',
    fixValidationErrors: 'Please fix validation errors before publishing',
    reorderError: 'Failed to reorder components',
    updateError: 'Failed to update component',
    replacedContent: (title: string) => `Replaced content with "${title}"`,
    premiumGranted: (count: number) => `Successfully granted ${count} premium ving${count !== 1 ? 's' : ''}`,
    premiumEnabled: 'Premium status enabled',
    premiumDisabled: 'Premium status removed',
    premiumError: 'Failed to update premium status',
    fileReplaceSuccess: 'File replaced successfully. Processing will begin shortly.',
  },

  /**
   * Component operations
   */
  component: {
    addSuccess: 'Component added',
    addError: 'Failed to add component',
    deleteSuccess: 'Component deleted',
    deleteError: 'Failed to delete component',
    uploadSuccess: (fileName: string) => `${fileName} uploaded successfully`,
  },

  /**
   * Invitation operations
   */
  invitation: {
    deleteSuccess: 'The invitation was successfully deleted.',
    deleteError: 'Failed to delete invitation',
    alreadySent: 'This invitation was already sent, so it can no longer be deleted.',
    rescheduleSuccess: 'Invitation rescheduled',
    rescheduleError: 'Failed to reschedule invitation',
    updateSuccess: 'Invitation updated successfully',
    scormImportSuccess: 'SCORM package imported successfully',
  },

  /**
   * Checklist operations
   */
  checklist: {
    createSuccess: 'Checklist created successfully',
    createError: 'Failed to create checklist',
    copySuccess: 'Checklist copied successfully',
    copyError: 'Failed to copy checklist',
    reviseError: 'Failed to create revision',
    publishSuccess: 'Checklist published successfully',
    publishError: 'Failed to publish checklist',
    deleteSuccess: 'Checklist deleted successfully',
    deleteError: 'Failed to delete checklist',
    archiveSuccess: 'Checklist archived successfully',
    archiveError: 'Failed to archive checklist',
    unarchiveSuccess: 'Checklist unarchived successfully',
    unarchiveError: 'Failed to unarchive checklist',
    ownerChangedSuccess: 'Owner changed successfully',
    ownerChangedError: 'Failed to change owner',
    fixValidationErrors: 'Please fix validation errors before publishing',
    premiumGranted: (count: number) => `Successfully granted ${count} premium checklist${count !== 1 ? 's' : ''}`,
    premiumEnabled: 'Premium status enabled',
    premiumDisabled: 'Premium status removed',
    premiumError: 'Failed to update premium status',
    replacedContent: (title: string) => `Replaced content with "${title}"`,
  },

  /**
   * Contact operations
   */
  contact: {
    archiveSuccess: 'Contact archived successfully',
    archiveError: 'Failed to archive contact',
    unarchiveSuccess: 'Contact unarchived successfully',
    unarchiveError: 'Failed to unarchive contact',
    /** Q-530 a (card 1545): the list bulk archive/unarchive left n contacts out. */
    bulkLeftOut: (action: 'archive' | 'unarchive', count: number) =>
      `Couldn't ${action} ${count} ${count === 1 ? 'contact' : 'contacts'}.`,
    /** #807 / vingapi#1093: the bulk archive answered 2xx but refused this
     *  contact (`refused_internal_contact_ids`) — a Ving support contact. */
    archiveRefusedInternal:
      "This is a Ving support contact, so it can't be archived here. It is archived by off-boarding when the staff member's access ends.",
    unarchiveRefusedInternal:
      "This is a Ving support contact, so it can't be restored here. It follows the staff member's account.",
    ownerChangedSuccess: 'Owner changed successfully',
    ownerChangedError: 'Failed to change owner',
    createSuccess: 'Contact created successfully',
    mergeSuccess: 'Contacts merged successfully',
    customFilterCreated: 'Custom filter created successfully',
    customFilterError: 'Failed to create custom filter. Please try again.',
    customFilterUpdated: 'Custom filter updated successfully',
    customFilterUpdateError: 'Failed to update custom filter. Please try again.',
    customFilterDeleted: 'Custom filter deleted',
    customFilterDeleteError: 'Failed to delete custom filter. Please try again.',
    provisionError: 'Failed to provision user accounts',
    attributesUpdated: 'Attributes updated successfully',
    // Provision OUTCOME text lives in features/contacts/provisionFeedback.ts —
    // it's composed per skip reason together with the banner severity, not a
    // fixed string.
  },

  /**
   * Admin tools (work queue, alerts)
   */
  admin: {
    queueDeleteFailed: 'Failed to delete queue item — it may have already been dispatched to a worker.',
    historyDeleteFailed: 'Failed to delete history item. Please try again.',
    historyBulkDeleteFailed: 'Failed to delete history rows. Please try again.',
    historyRetryFailed: 'Failed to retry work item. Please try again.',
    alertsDeleteFailed: 'Failed to delete alerts. Please try again.',
    alertConditionUpdateFailed:
      'Failed to update the alert condition — the change was not saved. It may already have moved to another state; the list has been refreshed.',
  },

  /**
   * Authentication operations
   */
  auth: {
    signInSuccess: 'Signed in successfully',
    signInFailed: 'Sign in failed. Please try again.',
    /** Q-678 (#1566 a): sign-in lockout; the fallback when the 423 carries no usable server message. */
    signInLocked: 'Too many tries. Try again in an hour.',
    /** Q-678 (#1566 a): code / address-verification lockout; same fallback rule. */
    verifyLocked: 'Too many tries. Try again in 15 minutes.',
    /** Q-506 (#1566 a): sign-in 429. */
    signInSlowDown: 'Too many tries. Try again in a minute.',
    signUpSuccess: 'Account created successfully!',
    verificationSent: 'Verification code sent! Check your email or phone.',
    verificationFailed: 'Verification code is wrong. Please try again.',
    magicLinkFailed: 'Email or Phone is wrong. Please enter valid one.',
    logoutSuccess: 'Logged out successfully',
    passwordResetSuccess: 'Password reset link sent',
    /**
     * vingreact#106 — a dead sign-in/reset link redirects to
     * `/login?error=<message>`. Every one of these messages carries a path
     * forward, because the person arrived here holding a link that no longer
     * works and the bare form told them nothing.
     */
    linkFailure:
      "That sign-in link has expired or was already used. Enter your email or phone and we'll send a fresh one.",
    /** Same failure, reached through a password-reset link (`?reset=1` hint). */
    resetLinkFailure:
      'That password reset link has expired or was already used. Use "Forgot Password?" below to request a new one.',
    /**
     * A seat problem, not a link problem: offering a fresh link would send the
     * person round the same loop. The administrator is the only path forward.
     */
    accountDeactivated:
      "This account has been deactivated. Contact your organization's administrator.",
    /**
     * /sso failure card. It is reached by magic-link and token arrivals as well
     * as social ones, so the copy leads with the link and mentions social
     * second instead of blaming an account the person may never have used.
     */
    ssoFailureGuidance:
      "We couldn't sign you in with that link. If you were using a social account, try again — or go back and sign in with your email or phone.",
    /**
     * vingapi#2440 / vingreact#1992 -- the /sso failure card for a coded
     * failure. The server sends a code and a provider display name, never
     * words; these are the owner's words.
     *
     * Q-652 (`adp_not_enabled`): title and guidance, exactly as approved,
     * including "need enabled".
     */
    ssoAdpNotEnabledTitle: 'ADP Sign-in failed',
    ssoAdpNotEnabledGuidance:
      'ADP has returned an error. This might need enabled for your ADP account.',
    /** Q-653 (`provider_error`, `expired_state` after the retry, any unknown code): the title. */
    ssoFailedTitle: (provider: string) => `${provider} Sign-in failed`,
    /** Q-653: the guidance line under {@link ssoFailedTitle}. */
    ssoProviderErrorGuidance: (provider: string) =>
      `${provider} has returned an error. Try again, or sign in with your email or phone.`,
    /**
     * Forgot-password confirmation. Conditional by design: vingapi declines
     * unknown/archived addresses silently to avoid account enumeration, so
     * promising delivery unconditionally is a lie to exactly those people.
     */
    resetLinkSent: 'If that address is registered, a link is on the way.',
    /**
     * vingreact#106 — a wrong password. It used to read "Sign in failed" and
     * bounce the person back to the identifier step, which threw away the one
     * thing they had typed correctly. The message names what actually
     * happened, and the two affordances below it are the only two ways out.
     */
    passwordMismatch:
      "That password didn't match. Try again, or sign in with a link instead — no password needed.",
    /**
     * Interceptor bounce reasons (`/login/signIn?reason=…`). A 401 mid-app used
     * to land here with no explanation at all.
     */
    sessionExpired: 'Your session ended. Please sign in again.',
    sessionRejected: "We couldn't verify your session. Please sign in again.",
    /**
     * #466 / #1567 — the carrier rejected the phone number synchronously; the
     * person cannot be texted. The client owns these words (owner Q-642
     * all-accept 10-23, Q-506 10-14): the server's `message` is never shown.
     */
    methodUnreachable:
      "We can't text this number. Try your email, or ask your admin to fix the number.",
    /** 429 on the link / reset forms — previously rendered raw axios text. */
    tooManyRequests: 'Too many requests. Please wait a minute and try again.',
    /** /sso exchange failed with a detail we do not map; never render the raw detail. */
    tokenExchangeFailed: "We couldn't complete your sign in. Please go back and try again.",
    /**
     * #308 (chunk F) — /login/verify with a correct code but no token back
     * (204: the method was verified for someone else's in-flight session, or
     * any other no-token success). The method is proven; sign-in is a
     * separate step.
     */
    verifiedPleaseSignIn: 'Verified. Please sign in.',
  },
} as const;

export type AlertMessageCategory = keyof typeof alertMessages;
